import React, { useCallback } from 'react';
import type { Company } from '../../types/company';
import { formatDate, getFullName, getInitial } from '../../utils/dateFormatter';

interface CompanyRowProps {
  company: Company;
  onClick: (company: Company) => void;
}

export const CompanyRow: React.FC<CompanyRowProps> = ({ company, onClick }) => {
  const fullName = getFullName(company.first_name, company.last_name);
  const initial = getInitial(company.first_name);
  const formattedDate = formatDate(company.created_date);

  const handleClick = useCallback(() => {
    onClick(company);
  }, [company, onClick]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(company);
    }
  }, [company, onClick]);

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="row"
      tabIndex={0}
      aria-label={`View analytics for ${fullName}`}
      className="block md:grid md:grid-cols-12 gap-4 px-4 sm:px-6 py-4 hover:bg-[var(--bg-hover)] hover:shadow-sm cursor-pointer transition-all duration-150 group focus:outline-none focus:ring-2 focus:ring-[#EF4421] focus:ring-inset"
    >
      {/* Name Column */}
      <div className="col-span-12 md:col-span-3 flex items-center space-x-3 mb-3 md:mb-0">
        <div 
          className="w-10 h-10 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-primary)] border border-[var(--border-color)] flex items-center justify-center font-semibold"
          aria-hidden="true"
        >
          {initial}
        </div>
        <div>
          <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-[#EF4421] transition-colors">
            {fullName}
          </p>
          <p className="text-xs text-[var(--text-muted)] flex items-center mt-0.5">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Created: {formattedDate}</span>
          </p>
        </div>
      </div>

      {/* Email Column */}
      <div className="col-span-12 md:col-span-3 flex items-center mb-2 md:mb-0" role="cell">
        <span className="md:hidden font-semibold text-xs text-[var(--text-muted)] mr-2 uppercase">Email:</span>
        <p className="text-sm text-[var(--text-secondary)] truncate max-w-[250px] break-words" title={company.email}>
          {company.email}
        </p>
      </div>

      {/* Address Column */}
      <div className="col-span-12 md:col-span-3 flex items-center mb-2 md:mb-0" role="cell">
        <span className="md:hidden font-semibold text-xs text-[var(--text-muted)] mr-2 uppercase">Address:</span>
        <p className="text-sm text-[var(--text-secondary)] truncate max-w-[250px] break-words" title={company.address}>
          {company.address}
        </p>
      </div>

      {/* Phone Column */}
      <div className="col-span-12 md:col-span-2 flex items-center mb-2 md:mb-0" role="cell">
        <span className="md:hidden font-semibold text-xs text-[var(--text-muted)] mr-2 uppercase">Phone:</span>
        <p className="text-sm text-[var(--text-secondary)]">{company.phone}</p>
      </div>

      {/* Company Column */}
      <div className="col-span-12 md:col-span-1 flex items-center justify-between" role="cell">
        <div className="flex items-center">
          <span className="md:hidden font-semibold text-xs text-[var(--text-muted)] mr-2 uppercase">Company:</span>
          <span className="text-sm text-[var(--text-secondary)]">
            {company.company_name}
          </span>
        </div>
        <svg 
          className="w-5 h-5 text-[var(--text-muted)] group-hover:text-[#EF4421] transition-colors ml-2" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};
